import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  CircularProgress,
  Snackbar,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import {
  ViewListOutlined as ViewListIcon,
  ViewModuleOutlined as ViewModuleIcon,
} from '@mui/icons-material';
import {
  ListPageScaffold,
  ListPageMain,
  ListPageFiltersSection,
} from '../../components/common/list-page';
import InventoryStats from '../../components/inventory/InventoryStats';
import InventoryQueryFilters from '../../components/inventory/InventoryQueryFilters';
import { CategoriesService } from '../../api/services/CategoriesService';
import { TagsService } from '../../api/services/TagsService';
import { MaterialsService } from '../../api/services/MaterialsService';
import { ColorsService } from '../../api/services/ColorsService';
import { Category } from '../../api/models/Category';
import { Tag } from '../../api/models/Tag';
import {
  InventoryItem,
  PaginationInfo,
  InventoryFilterState,
  DEFAULT_WAREHOUSE_INVENTORY_SORT,
  WarehouseInventorySortField,
} from '../../types/inventory';
import { WarehouseInventoryProductsService } from '../../api/services/WarehouseInventoryProductsService';
import { buildWarehouseInventoryFindAllArgs } from '../../utils/inventory/warehouseInventoryListQuery';
import { mapWarehouseInventoryProductsToInventoryItems } from '../../utils/inventory/mapWarehouseInventoryProductToInventoryItem';
import { WarehouseInventorySummaryDto } from '../../api/models/WarehouseInventorySummaryDto';
import { LocationsService } from '../../api/services/LocationsService';
import { Location } from '../../api/models/Location';
import InventoryDetailModal from '../../components/inventory/InventoryDetailModal';
import InventoryRequestModal from '../../components/inventory/InventoryRequestModal';
import { BranchTable, InventoryGrid } from '../../components/inventory/InventoryQueryTable';
import CommonPagination from '../../components/common/Pagination';
import EmptyStateMessage from '../../components/common/EmptyStateMessage';
import { selectCurrentUser } from '../../redux/slices/auth.slice';
import { useInventoryGeneralRequestPermissions } from '../../hooks/useInventoryRequestPermissions';
import { resolveLockedOriginLocationName } from '../../utils/inventory/inventoryRequestSelection';
import {
  INV_AVAILABILITY_IN_REQUEST_LABEL,
  INV_AVAILABILITY_WRITE_OFF_LABEL,
  INV_STATUS_BRANCH_LABEL,
  INV_STATUS_WAREHOUSE_LABEL,
} from '../../constants/inventory-status-labels';
import { ProductsService } from '../../api/services/ProductsService';

const InventoryGeneralQueryPage: React.FC = () => {
  /** Inventario general: consulta global; el filtro de ubicación no se fuerza por sucursal asignada. */
  const isLocationLocked = false;
  const currentUser = useSelector(selectCurrentUser);
  const { canCreateRequest } = useInventoryGeneralRequestPermissions();

  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [selectedItemsDetails, setSelectedItemsDetails] = useState<InventoryItem[]>([]);
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [inventoryStats, setInventoryStats] = useState<WarehouseInventorySummaryDto>({
    totalProducts: 0,
    totalValue: 0,
    totalCostValue: 0,
    writeOffCount: 0,
    statusSummary: [],
    availabilityStatusSummary: [],
    mostCommonCondition: '',
    lastActivityDate: '',
  });
  const [catalogProductTotal, setCatalogProductTotal] = useState<number>(0);
  const [isStatsLoading, setIsStatsLoading] = useState<boolean>(true);
  const [locations, setLocations] = useState<Location[]>([]);
  const [isLocationsLoading, setIsLocationsLoading] = useState<boolean>(true);

  const [productTypes, setProductTypes] = useState<string[]>([]);
  const [tagsList, setTagsList] = useState<Tag[]>([]);
  const [materials, setMaterials] = useState<{ id: number; name: string }[]>([]);
  const [colorsCatalog, setColorsCatalog] = useState<{ id: number; name: string }[]>([]);
  const [, setIsMetadataLoading] = useState<boolean>(true);

  const [filters, setFilters] = useState<InventoryFilterState>({
    searchTerm: '',
    createdAtStart: '',
    createdAtEnd: '',
    status: '',
    locationId: '',
    categoryName: '',
    recordCode: '',
    searchTag: '',
    materialId: '',
    diameterSize: '',
    color: '',
    workshopCode: '',
    warehousePlacementStatus: '',
    branchAvailabilityStatus: '',
    weightGramsMin: undefined,
    weightGramsMax: undefined,
    salePriceMin: undefined,
    salePriceMax: undefined,
    salePricePerGramMin: undefined,
    salePricePerGramMax: undefined,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [inventorySort, setInventorySort] = useState<{
    sortBy: WarehouseInventorySortField;
    sortOrder: 'asc' | 'desc';
  }>(DEFAULT_WAREHOUSE_INVENTORY_SORT);

  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState<boolean>(false);

  const [viewMode, setViewMode] = useState<'table' | 'grid'>('table');
  const [searchTrigger, setSearchTrigger] = useState(0);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const fetchLocations = useCallback(async () => {
    setIsLocationsLoading(true);
    try {
      const response = await LocationsService.locationsControllerFindAll();
      let locationsList: Location[] = [];
      if (response && Array.isArray(response.data)) {
        locationsList = response.data as Location[];
      } else if (response && Array.isArray(response)) {
        locationsList = response as Location[];
      }
      setLocations(locationsList);
    } catch (error) {
      console.error('Error fetching locations:', error);
      setLocations([]);
    } finally {
      setIsLocationsLoading(false);
    }
  }, []);

  const fetchMetadata = useCallback(async () => {
    setIsMetadataLoading(true);
    try {
      const [categoriesResponse, tagsResponse, materialsResponse, colorsResponse] =
        await Promise.all([
          CategoriesService.categoriesControllerFindAll(1, 1000),
          TagsService.tagsControllerFindAll(1, 1000),
          MaterialsService.materialsControllerFindAll(1, 1000),
          ColorsService.colorsControllerFindAll(1, 1000),
        ]);

      let categoriesList: Category[] = [];
      if (categoriesResponse && Array.isArray(categoriesResponse.data)) {
        categoriesList = categoriesResponse.data as Category[];
      } else if (categoriesResponse && Array.isArray(categoriesResponse)) {
        categoriesList = categoriesResponse as Category[];
      }
      setProductTypes(categoriesList.map((cat) => cat.name));

      let tagsListRaw: Tag[] = [];
      if (tagsResponse && Array.isArray(tagsResponse.data)) {
        tagsListRaw = tagsResponse.data as Tag[];
      } else if (tagsResponse && Array.isArray(tagsResponse)) {
        tagsListRaw = tagsResponse as Tag[];
      }
      setTagsList(tagsListRaw);

      const materialsList = Array.isArray(materialsResponse?.data)
        ? materialsResponse.data
        : Array.isArray(materialsResponse)
          ? materialsResponse
          : [];
      setMaterials(
        materialsList.map((material: { id: number; name: string }) => ({
          id: material.id,
          name: material.name,
        })),
      );

      const colorsList = Array.isArray(colorsResponse?.data)
        ? colorsResponse.data
        : Array.isArray(colorsResponse)
          ? colorsResponse
          : [];
      setColorsCatalog(
        colorsList.map((c: { id: number; name: string }) => ({
          id: c.id,
          name: c.name,
        })),
      );
    } catch (error) {
      console.error('Error fetching metadata:', error);
      setProductTypes([]);
      setTagsList([]);
      setMaterials([]);
      setColorsCatalog([]);
    } finally {
      setIsMetadataLoading(false);
    }
  }, []);

  const fetchCatalogTotal = useCallback(async () => {
    try {
      const res = await ProductsService.productsControllerFindAll(1, 1);
      const total = res?.meta?.total ?? res?.meta?.totalItems ?? 0;
      setCatalogProductTotal(typeof total === 'number' ? total : 0);
    } catch (e) {
      console.error('Error fetching catalog product total', e);
      setCatalogProductTotal(0);
    }
  }, []);

  const fetchInventoryStats = useCallback(async (currentFilters: InventoryFilterState) => {
    setIsStatsLoading(true);
    try {
      const response =
        await WarehouseInventoryProductsService.warehouseInventoryProductsControllerGetSummary(
          currentFilters.locationId && currentFilters.locationId !== '0'
            ? Number(currentFilters.locationId)
            : undefined,
          undefined,
          currentFilters.warehousePlacementStatus || undefined,
          undefined,
          undefined,
          undefined,
          currentFilters.searchTerm || undefined,
          currentFilters.categoryName && currentFilters.categoryName !== 'Todos'
            ? currentFilters.categoryName
            : undefined,
          currentFilters.createdAtStart || undefined,
          currentFilters.createdAtEnd || undefined,
          currentFilters.recordCode || undefined,
          currentFilters.branchAvailabilityStatus || undefined,
          currentFilters.searchTag || undefined,
          currentFilters.materialId ? Number(currentFilters.materialId) : undefined,
          currentFilters.diameterSize || undefined,
          currentFilters.color || undefined,
          currentFilters.workshopCode || undefined,
          currentFilters.weightGramsMin,
          currentFilters.weightGramsMax,
          currentFilters.salePriceMin,
          currentFilters.salePriceMax,
          currentFilters.salePricePerGramMin,
          currentFilters.salePricePerGramMax,
          undefined, // excludeCancelledAvailability
          undefined, // warehousePlacementOnly
          undefined, // assignedPlacementOnly
        );
      setInventoryStats(response);
    } catch (error) {
      console.error('Error fetching inventory stats:', error);
    } finally {
      setIsStatsLoading(false);
    }
  }, []);

  const fetchInventoryData = useCallback(
    async (currentFilters: InventoryFilterState) => {
      setIsLoading(true);
      try {
        const response =
          await WarehouseInventoryProductsService.warehouseInventoryProductsControllerFindAll(
            ...buildWarehouseInventoryFindAllArgs({
              page: currentPage,
              limit: itemsPerPage,
              filters: currentFilters,
              placementStatus: currentFilters.warehousePlacementStatus || undefined,
              availabilityStatus: currentFilters.branchAvailabilityStatus || undefined,
              sortBy: inventorySort.sortBy as string,
              sortOrder: inventorySort.sortOrder,
              includeNumericFilters: true,
            }),
          );

        setInventory(
          mapWarehouseInventoryProductsToInventoryItems(response.data, {
            labelVariant: 'general',
          }),
        );
        setTotalItems(response.meta?.total || 0);
      } catch (error) {
        console.error('Error fetching inventory data:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [currentPage, itemsPerPage, inventorySort.sortBy, inventorySort.sortOrder],
  );

  const handleInventorySortChange = useCallback((field: WarehouseInventorySortField) => {
    setInventorySort((prev) => {
      if (prev.sortBy === field) {
        return {
          sortBy: field,
          sortOrder: prev.sortOrder === 'asc' ? 'desc' : 'asc',
        };
      }
      return { sortBy: field, sortOrder: 'asc' };
    });
    setCurrentPage(1);
  }, []);

  const currentFiltersRef = useRef(filters);
  useEffect(() => {
    currentFiltersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    fetchInventoryData(currentFiltersRef.current);
    fetchInventoryStats(currentFiltersRef.current);
  }, [fetchInventoryData, fetchInventoryStats, searchTrigger]);

  useEffect(() => {
    fetchLocations();
    fetchMetadata();
    fetchCatalogTotal();
  }, [fetchLocations, fetchMetadata, fetchCatalogTotal]);

  useEffect(() => {
    setSearchTrigger(1);
  }, []);

  const paginationInfo: PaginationInfo = useMemo(() => {
    return {
      currentPage,
      totalPages: Math.ceil(totalItems / itemsPerPage),
      itemsPerPage,
      totalItems,
    };
  }, [currentPage, itemsPerPage, totalItems]);

  const handleFilterChange = useCallback((newFilters: InventoryFilterState) => {
    setFilters({
      ...newFilters,
      status: '',
    });
    setCurrentPage(1);
    setSearchTrigger((prev) => prev + 1);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleItemsPerPageChange = useCallback((newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  }, []);

  const handleViewDetails = (id: string) => {
    const item = inventory.find((i) => i.id === id);
    if (item) {
      setSelectedItem(item);
      setIsDetailModalOpen(true);
    }
  };

  const formattedCategories = useMemo(() => {
    return productTypes.map((tipo, index) => ({
      id: index + 1,
      name: tipo,
    }));
  }, [productTypes]);

  const formattedLocations = useMemo(() => {
    const allLocationsOption = { id: 0, name: 'Todas las Ubicaciones' };
    if (isLocationsLoading) {
      return [allLocationsOption];
    }
    return [allLocationsOption, ...locations];
  }, [locations, isLocationsLoading]);

  const formattedTags = useMemo(() => {
    return tagsList.map((tag) => ({
      id: tag.id,
      name: tag.name,
    }));
  }, [tagsList]);

  const warehousePlacementStatuses = useMemo(
    () => [
      { id: 'UNASSIGNED', name: INV_STATUS_WAREHOUSE_LABEL },
      { id: 'IN_TRANSIT', name: 'En Tránsito' },
      { id: 'ASSIGNED', name: INV_STATUS_BRANCH_LABEL },
    ],
    [],
  );

  const branchAvailabilityStatuses = useMemo(
    () => [
      { id: 'AVAILABLE', name: 'Disponible' },
      { id: 'SOLD', name: 'Vendido' },
      { id: 'RESERVED', name: 'Bloqueada' },
      { id: 'PARTIAL_PAYMENT', name: 'Pago parcial' },
      { id: 'IN_REQUEST', name: INV_AVAILABILITY_IN_REQUEST_LABEL },
      { id: 'CANCELLED', name: INV_AVAILABILITY_WRITE_OFF_LABEL },
    ],
    [],
  );

  const itemById = useMemo(() => new Map(inventory.map((item) => [item.id, item])), [inventory]);

  const lockedOriginLocationName = useMemo(
    () => resolveLockedOriginLocationName(selectedItems, itemById),
    [selectedItems, itemById],
  );

  const requestModalLocations = useMemo(
    () =>
      locations.map((location) => ({
        id: location.id,
        name: location.name,
        type: location.type as 'WAREHOUSE' | 'BRANCH' | undefined,
      })),
    [locations],
  );

  useEffect(() => {
    const newDetails = [...selectedItemsDetails];
    let changed = false;

    inventory.forEach((item) => {
      if (selectedItems.includes(item.id)) {
        const existingIndex = newDetails.findIndex((detail) => detail.id === item.id);
        if (existingIndex === -1) {
          newDetails.push(item);
          changed = true;
        }
      }
    });

    if (changed) {
      setSelectedItemsDetails(newDetails);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventory, selectedItems]);

  const handleSelectItems = useCallback((selectedIds: string[]) => {
    setSelectedItems(selectedIds);
    setSelectedItemsDetails((prev) => prev.filter((item) => selectedIds.includes(item.id)));
  }, []);

  const handleOpenRequestModal = useCallback(() => {
    if (selectedItems.length > 0) {
      setIsRequestModalOpen(true);
    }
  }, [selectedItems.length]);

  const handleCloseRequestModal = useCallback(() => {
    setIsRequestModalOpen(false);
  }, []);

  const handleRequestSuccess = useCallback(() => {
    setSelectedItems([]);
    setSelectedItemsDetails([]);
    setIsRequestModalOpen(false);
    setSearchTrigger((prev) => prev + 1);
    setNotification({
      open: true,
      message: 'Solicitud de inventario creada con éxito.',
      severity: 'success',
    });
  }, []);

  const handleCloseNotification = useCallback(() => {
    setNotification((prev) => ({ ...prev, open: false }));
  }, []);

  const handleRemoveRequestProduct = useCallback((productId: string) => {
    setSelectedItems((prev) => prev.filter((id) => id !== productId));
    setSelectedItemsDetails((prev) => prev.filter((item) => item.id !== productId));
  }, []);

  const enableRequestSelection = canCreateRequest;

  const dummyStatuses = useMemo(() => [{ id: '_', name: '' }], []);

  return (
    <ListPageScaffold>
      <ListPageMain sectionGap={1}>
        {isStatsLoading ? (
          <Box
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 1,
              p: 2,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '120px',
            }}
          >
            <CircularProgress size={40} />
          </Box>
        ) : (
          <Box sx={{ width: '100%' }}>
            <InventoryStats
              totalProducts={inventoryStats.totalProducts}
              totalValue={inventoryStats.totalValue}
              totalCostValue={inventoryStats.totalCostValue}
              writeOffCount={inventoryStats.writeOffCount ?? 0}
              statusSummary={inventoryStats.statusSummary}
              lastActivityDate={inventoryStats.lastActivityDate}
              variant="branch"
              availabilityStatusSummary={inventoryStats.availabilityStatusSummary}
              compactSpacing
              kpiPermissionProfile="general"
              catalogProductTotal={catalogProductTotal}
            />
          </Box>
        )}

        <ListPageFiltersSection>
          <InventoryQueryFilters
            categories={formattedCategories}
            locations={formattedLocations}
            statuses={dummyStatuses}
            warehousePlacementStatuses={warehousePlacementStatuses}
            branchAvailabilityStatuses={branchAvailabilityStatuses}
            filterVariant="general"
            tags={formattedTags}
            colors={colorsCatalog}
            materials={materials}
            onFilterChange={handleFilterChange}
            initialFilters={filters}
            selectedItems={selectedItems}
            onRequestClick={enableRequestSelection ? handleOpenRequestModal : undefined}
            isLocationLocked={isLocationLocked}
            hideSecondaryFilterPlaceholders
            rightControls={
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_, newMode: 'table' | 'grid' | null) =>
                  newMode != null && setViewMode(newMode)
                }
                aria-label="vista de inventario"
                size="small"
                sx={(theme) => ({
                  '& .MuiToggleButton-root': {
                    borderColor: theme.palette.divider,
                    color: theme.palette.text.secondary,
                    '&.Mui-selected': {
                      backgroundColor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(6, 182, 212, 0.2)'
                          : 'rgba(6, 182, 212, 0.12)',
                      color: theme.palette.mode === 'dark' ? theme.palette.info.light : '#0891b2',
                      borderColor: theme.palette.info.main,
                      '&:hover': {
                        backgroundColor:
                          theme.palette.mode === 'dark'
                            ? 'rgba(6, 182, 212, 0.28)'
                            : 'rgba(6, 182, 212, 0.18)',
                      },
                    },
                  },
                })}
              >
                <ToggleButton value="table" aria-label="vista tabla">
                  <Tooltip title="Vista tabla" arrow>
                    <ViewListIcon />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="grid" aria-label="vista cuadrícula">
                  <Tooltip title="Vista cuadrícula" arrow>
                    <ViewModuleIcon />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            }
          />
        </ListPageFiltersSection>

        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 4,
              minHeight: '200px',
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            {inventory.length > 0 && (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 0,
                  border: 'none',
                  bgcolor: 'transparent',
                  overflow: 'hidden',
                  minHeight: 0,
                  px: 0,
                }}
              >
                {viewMode === 'table' ? (
                  <BranchTable
                    variant="general"
                    items={inventory}
                    onViewDetails={handleViewDetails}
                    pagination={paginationInfo}
                    onPageChange={handlePageChange}
                    onItemsPerPageChange={handleItemsPerPageChange}
                    selectedItems={selectedItems}
                    onSelectItems={handleSelectItems}
                    enableRequestSelection={enableRequestSelection}
                    lockedOriginLocationName={lockedOriginLocationName}
                    hidePagination={true}
                    sortBy={inventorySort.sortBy}
                    sortOrder={inventorySort.sortOrder}
                    onSortChange={handleInventorySortChange}
                  />
                ) : (
                  <InventoryGrid
                    variant="branch"
                    items={inventory}
                    onViewDetails={handleViewDetails}
                    selectedItems={selectedItems}
                    onSelectItems={handleSelectItems}
                    enableRequestSelection={enableRequestSelection}
                    lockedOriginLocationName={lockedOriginLocationName}
                  />
                )}
              </Box>
            )}

            {inventory.length === 0 && !isLoading && (
              <EmptyStateMessage message="No se encontraron piezas en inventario" minHeight={200} />
            )}

            {inventory.length > 0 && (
              <Box>
                <CommonPagination
                  currentPage={currentPage}
                  itemsPerPage={itemsPerPage}
                  totalItems={totalItems}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={(event) => {
                    const newItemsPerPage = parseInt(event.target.value as string);
                    handleItemsPerPageChange(newItemsPerPage);
                  }}
                />
              </Box>
            )}
          </>
        )}
      </ListPageMain>

      <InventoryDetailModal
        open={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        item={selectedItem}
        onEditItem={() => {}}
        allowEditInBranch={false}
        onSave={() => {
          fetchInventoryData(currentFiltersRef.current);
          fetchInventoryStats(currentFiltersRef.current);
        }}
        onItemUpdate={(updatedItem) => {
          setSelectedItem(updatedItem);
          setInventory((prev) =>
            prev.map((row) => (row.id === updatedItem.id ? updatedItem : row)),
          );
        }}
        blockHistoryRefreshKey={searchTrigger}
      />

      <InventoryRequestModal
        open={isRequestModalOpen}
        onClose={handleCloseRequestModal}
        selectedProductIds={selectedItems}
        inventoryItems={selectedItemsDetails}
        locations={requestModalLocations}
        defaultDestinationLocationId={currentUser?.primaryLocationId ?? null}
        onSuccess={handleRequestSuccess}
        onRemoveProduct={handleRemoveRequestProduct}
      />

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </ListPageScaffold>
  );
};

export default InventoryGeneralQueryPage;
